/**
 * Registry of drafts open in an editor right now.
 *
 * The review card in chat, the pending-changes drawer and the live agent path
 * all need to reach the editor that has a given draft open: to accept a change
 * in the page instead of on the server, to reveal a passage, to apply a
 * suggestion where the attorney can see it land. The editor registers itself
 * here for as long as it is mounted with the live draft (never for read-only
 * previews such as version history).
 */
import { useEffect, useState } from "react";
import { create } from "zustand";

import type { DraftSaveTrigger } from "../../api/drafts-v2";
import type { DocxEditorMode, DocxSurfaceHandle } from "./surface";

export type OpenDraftApi = {
  draftId: string;
  /** The mounted surface; `null` until the document has painted. */
  surface: () => DocxSurfaceHandle | null;
  /**
   * Persist the editor's bytes; resolves once the server has them. Callers
   * name what caused the save (`trigger`) so version history reads right.
   */
  save: (options?: {
    force?: boolean;
    trigger?: DraftSaveTrigger;
  }) => Promise<void>;
  /** Monotonic count of local edits since the document was opened or reloaded. */
  revision: () => number;
  /** Whether there are edits the server does not have yet. */
  isDirty: () => boolean;
  /** Fires after every committed edit, comment change, save and reload. */
  subscribe: (listener: () => void) => () => void;
  readOnly: boolean;
  /** The draft's name as the tab shows it. */
  title: () => string;
  /** The editing mode the attorney has the editor in. */
  mode: () => DocxEditorMode;
};

type OpenDraftsState = {
  drafts: Record<string, OpenDraftApi>;
  register: (api: OpenDraftApi) => () => void;
};

export const useOpenDraftsStore = create<OpenDraftsState>((set, get) => ({
  drafts: {},
  register: (api) => {
    set((state) => ({ drafts: { ...state.drafts, [api.draftId]: api } }));
    return () => {
      // Only the registration that is still current unregisters; a remount
      // may already have replaced it.
      if (get().drafts[api.draftId] !== api) return;
      set((state) => {
        const { [api.draftId]: _gone, ...rest } = state.drafts;
        return { drafts: rest };
      });
    };
  }
}));

/** The open editor for a draft, or `null` when it is not open. */
export const getOpenDraft = (draftId: string): OpenDraftApi | null =>
  useOpenDraftsStore.getState().drafts[draftId] ?? null;

export const useOpenDraft = (draftId: string): OpenDraftApi | null =>
  useOpenDraftsStore((state) => state.drafts[draftId] ?? null);

/**
 * A counter that ticks on every change the open editor reports, so hooks that
 * derive state from the live document (the tracked-change list) recompute.
 */
export const useOpenDraftTick = (api: OpenDraftApi | null): number => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!api) return undefined;
    setTick((t) => t + 1);
    return api.subscribe(() => setTick((t) => t + 1));
  }, [api]);
  return tick;
};
